import { useState, useCallback } from 'react';
import { useSendMessageMutation } from '~/api/chatbot/Chatbot.api';
import { ChatMessage, SendChatResponse } from '~/features/chatbot/types/chatbot.types';
import { extractApiErrorMessage } from '~/features/chatbot/utils/extractApiErrorMessage';
import { RECOVERY_MESSAGE } from '~/features/chatbot/constants/messages';

let messageCounter = 0;
const generateMessageId = (): string => `msg-${Date.now()}-${++messageCounter}`;

export interface UseChatSessionReturn {
    sessionId: string;
    messages: ChatMessage[];
    isLoading: boolean;
    error: string | null;
    sendMessage: (text: string) => Promise<void>;
    startNewSession: () => void;
    loadSession: (sessionId: string, existingMessages: ChatMessage[]) => void;
    setSessionError: (errorMessage: string) => void;
}

export const useChatSession = (): UseChatSessionReturn => {
    const [sessionId, setSessionId] = useState<string>('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [sendMessageMutation] = useSendMessageMutation();

    const sendMessage = useCallback(
        async (text: string) => {
            if (!text.trim()) {
                return;
            }

            setError(null);

            const userMessage: ChatMessage = {
                id: generateMessageId(),
                sessionId,
                content: text,
                role: 'USER',
                timestamp: new Date().toISOString(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setIsLoading(true);

            try {
                const request = sessionId
                    ? { message: text, sessionId }
                    : { message: text };

                const response: SendChatResponse =
                    await sendMessageMutation(request).unwrap();

                if (response.sessionId) {
                    setSessionId(response.sessionId);
                }

                const botMessage: ChatMessage = {
                    id: generateMessageId(),
                    sessionId: response.sessionId || sessionId,
                    content: response.response,
                    role: 'ASSISTANT',
                    timestamp: new Date().toISOString(),
                    isRecoveryMessage: response.response === RECOVERY_MESSAGE,
                };

                setMessages((prev) => [...prev, botMessage]);
            } catch (err: unknown) {
                const errorMessage = extractApiErrorMessage(err);
                setError(errorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [sessionId, sendMessageMutation]
    );

    const startNewSession = useCallback(() => {
        setSessionId('');
        setMessages([]);
        setIsLoading(false);
        setError(null);
    }, []);

    const loadSession = useCallback((loadedSessionId: string, existingMessages: ChatMessage[]) => {
        setSessionId(loadedSessionId);
        setMessages(existingMessages);
        setIsLoading(false);
        setError(null);
    }, []);

    const setSessionError = useCallback((errorMessage: string) => {
        setError(errorMessage);
    }, []);

    return {
        sessionId,
        messages,
        isLoading,
        error,
        sendMessage,
        startNewSession,
        loadSession,
        setSessionError,
    };
};
